import React from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { NotificationDTO, notificationService } from '../service/notificationService';
import { emitLocalEvent } from '../../shared/realtime/ws';

const MESSAGE_PREVIEW_MAX_CHARS = 160;

interface Props {
  notification: NotificationDTO;
  companyId: string;
  onClose: () => void;
  onLike?: () => void;
  onDislike?: () => void;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR'); // DD/MM/AAAA
}

export const NotificationToast: React.FC<Props> = ({
  notification,
  companyId,
  onClose,
  onLike,
  onDislike,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notificationService.like(notification.id);
      emitLocalEvent({ type: 'NOTIFICATION_UPDATED', company_id: companyId });
      onLike?.();
      onClose();
    } catch (error) {
      console.error('Erro ao dar like na notificação:', error);
    }
  };

  const handleDislike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notificationService.dislike(notification.id);
      emitLocalEvent({ type: 'NOTIFICATION_UPDATED', company_id: companyId });
      onDislike?.();
      onClose();
    } catch (error) {
      console.error('Erro ao dar dislike na notificação:', error);
    }
  };

  const renderTypeChip = (t: number) => {
    if (t === 1) {
      return <Chip label="Urgente" size="small" color="error" sx={{ fontWeight: 600, borderRadius: 1 }} />;
    }
    return <Chip label="Padrão" size="small" variant="outlined" sx={{ borderRadius: 1 }} />;
  };

  const shouldTruncate = (notification.message?.length || 0) > MESSAGE_PREVIEW_MAX_CHARS;
  const previewMessage = shouldTruncate
    ? `${notification.message.slice(0, MESSAGE_PREVIEW_MAX_CHARS).trimEnd()}...`
    : notification.message;
  const displayMessage = expanded ? notification.message : previewMessage;

  return (
    <Paper
      elevation={4}
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        p: 1.5,
        minWidth: 320,
        maxWidth: 400,
        borderLeft: notification.type === 1 ? '4px solid #d32f2f' : '4px solid #1976d2',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* título + data + close */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word', flex: 1 }}>
            {notification.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatDate(notification.created_at)}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{ ml: 0.5, p: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1 }}>
          {displayMessage}
        </Typography>
        {shouldTruncate && (
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          sx={(theme) => ({
            px: 0,
            mb: 1,
            minWidth: 'auto',
            textTransform: 'none',
            fontWeight: 600,
            color: theme.palette.mode === 'dark' ? 'common.white' : 'primary.main',
          })}
          >
            {expanded ? 'Ver menos' : 'Ver mais'}
          </Button>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            startIcon={<ThumbUpAltIcon />}
            onClick={handleLike}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            Gostei
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<ThumbDownAltIcon />}
            onClick={handleDislike}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            Não gostei
          </Button>

          <Box sx={{ flexGrow: 1 }} />
          {renderTypeChip(notification.type)}
        </Stack>
      </Box>
    </Paper>
  );
};

